import { useRef } from 'react';

import Grid2 from '@mui/material/Grid2';

import ListItem from '@webclient/components/pages/home/sections/bots/list/ListItem';
import LoadingSkeleton from '@webclient/components/pages/home/sections/bots/list/loading-skeleton';
import {
  StyledFormControl,
  StyledImageList,
  StyledRadioGroup,
} from '@webclient/components/pages/home/sections/bots/list/styles';

import CONFIG from '@webclient/lib/const/bots';
import createPublicImageUrl from '@webclient/lib/createPublicImageUrl';

import useAppDispatch from '@webclient/hooks/useAppDispatch';
import useAppSelector from '@webclient/hooks/useAppSelector';

import { setBotByUserId } from '@webclient/state/slices/app-settings';
import {
  selectBot,
  selectVoice,
} from '@webclient/state/slices/app-settings/userSettingSelector';

import { BotsResponseType } from '@webclient/shared-types/bots';

type Props = {
  bots: BotsResponseType;
  isLoading: boolean;
};

export default function List({ bots, isLoading }: Props) {
  const bot = useAppSelector(selectBot);
  const me = useAppSelector((state) => state.auth.me);
  const dispatch = useAppDispatch();
  const audioRef = useRef<HTMLAudioElement>(null);
  const voice = useAppSelector(selectVoice);
  const shouldAutoplayAudio = voice.autoplay;

  function handleBotChange(
    _event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) {
    const valueAsNumber = Number(value);
    const isItDefaultBot = valueAsNumber === CONFIG.DEFAULT_KNOWZ_AI_BOT.id;
    const bot = bots?.find((bot) => bot.id === valueAsNumber);
    if (!bot || !me?.uuid) return;
    if (shouldAutoplayAudio) {
      getAudioUrl(bot.introAudio);
    }
    dispatch(
      setBotByUserId({
        bot: {
          id: isItDefaultBot ? CONFIG.DEFAULT_KNOWZ_AI_BOT.id : valueAsNumber,
          name: isItDefaultBot ? CONFIG.DEFAULT_KNOWZ_AI_BOT.name : bot.name,
          image: isItDefaultBot ? CONFIG.DEFAULT_KNOWZ_AI_BOT.image : bot.image,
        },
        userId: me.uuid,
      }),
    );
  }

  function getAudioUrl(url: string | null) {
    const publicUrl = createPublicImageUrl(url);
    if (!publicUrl || !audioRef.current) return;
    audioRef.current.src = publicUrl;
    audioRef.current?.play();
  }

  return (
    <StyledFormControl>
      <StyledRadioGroup
        onChange={handleBotChange}
        defaultValue={bot.id ?? CONFIG.DEFAULT_KNOWZ_AI_BOT.id}
        value={bot.id ?? CONFIG.DEFAULT_KNOWZ_AI_BOT.id}
      >
        <StyledImageList
          minHeight={241}
          spacing={4}
          columns={12}
          container
        >
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <>
              <audio ref={audioRef} />
              {bots.map((bot) => {
                if (!bot) return null;
                return (
                  <Grid2 key={bot.id}>
                    <ListItem
                      getAudioUrl={getAudioUrl}
                      key={bot.id}
                      {...bot}
                    />
                  </Grid2>
                );
              })}
            </>
          )}
        </StyledImageList>
      </StyledRadioGroup>
    </StyledFormControl>
  );
}
